/* eslint-disable no-restricted-imports */
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useOpenDialog } from '../../routing/ModalRoute';
import { DIALOGS } from '../../routing/constants';
import { useTranslation } from '../../utils/useTranslation';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Typography } from '../typography/Typography';
import { CollectionCardSkeleton } from './CollectionCard';
import type { CollectionUI } from './types';

const GridRoot = styled(Stack)
    .withConfig({ displayName: 'GridRoot' })
    .attrs({
        spacing: 1,
        direction: 'row',
        flexWrap: 'wrap',
        p: { xs: 1, md: 0 },
        width: { xs: '100%', sm: 'calc(100% - 8px)' },
    })``;

interface CollectionsGridProps extends FlexProps {
    isLoading: boolean;
    collections: CollectionUI[];
    renderItem: (index: number) => ReactElement;
    stubMessage?: string;
    columnsCount?: number;
    loadMore?: VoidFunction;
}

export function CollectionsGrid(props: CollectionsGridProps) {
    const { isLoading, collections, renderItem, stubMessage, columnsCount, loadMore, ...other } =
        props;
    const t = useTranslation();
    const openDialog = useOpenDialog();
    const sentinelRef = useRef<HTMLDivElement>(null);

    const openCreateCollection = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    useEffect(() => {
        if (!loadMore || !sentinelRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 0 },
        );

        observer.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, [loadMore]);

    if (isLoading && collections.length === 0) {
        return (
            <GridRoot
                count={columnsCount ? columnsCount * 2 : 6}
                renderItem={(index) => (
                    <CollectionCardSkeleton key={index} columnsCount={columnsCount} />
                )}
                {...other}
            />
        );
    }

    if (collections.length === 0) {
        return (
            <Flex
                px={4}
                py={6}
                overflow="hidden"
                direction="column"
                alignItems="center"
                justifyContent="center"
                borderRadius={{ sm: 3 }}
                background="var(--background-primary)"
                {...other}
            >
                {stubMessage ? (
                    <Typography variant="body" color="secondary" textAlign="center">
                        {t(stubMessage)}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            as="h3"
                            variant="title3"
                            weight="bold"
                            mb={1}
                            textAlign="center"
                        >
                            {t('В избранном ничего нет')}
                        </Typography>
                        <Typography variant="body" color="secondary" textAlign="center">
                            {t(
                                'В этом разделе будут находиться посты, которые вы добавите из ленты',
                            )}
                        </Typography>
                        <Box pt={3}>
                            <Button onClick={openCreateCollection} size="m">
                                {t('Создать коллекцию')}
                            </Button>
                        </Box>
                    </>
                )}
            </Flex>
        );
    }

    return (
        <>
            <GridRoot count={collections.length} renderItem={renderItem} {...other} />
            {loadMore ? <div ref={sentinelRef} style={{ height: 1 }} /> : null}
            {isLoading && collections.length > 0 ? (
                <GridRoot
                    count={columnsCount ? columnsCount : 3}
                    renderItem={(index) => (
                        <CollectionCardSkeleton key={index} columnsCount={columnsCount} />
                    )}
                />
            ) : null}
        </>
    );
}
